/**
 * Chips and badges on Ledger tokens.
 *
 * - `tag` is the Ledger Chip: a 20px pill at `--text-12` 500. The palette
 *   carries the tone (gray neutral, green ok, yellow warn, red err, blue info,
 *   accent/orange accent), so the soft tint is its background and the soft-fg
 *   its text, as the colour rules ask.
 * - `badge` keeps serving the app's ~180 raw `<Badge>` labels, drawn the same
 *   way as a chip so a label reads the same wherever it sits. The Ledger count
 *   Badge is `~/components/ui/badge`, on top of this recipe.
 *
 * Spec: docs/design-system/DESIGN-SYSTEM.md, "Components 1: primitives".
 */
import { defineRecipe, defineSlotRecipe } from "@chakra-ui/react";
import { focusRing } from "./shared";

const pill = {
  borderRadius: "var(--radius-pill)",
  fontFamily: "var(--font-sans)",
  fontWeight: "var(--weight-medium)",
  lineHeight: "var(--leading-tight)",
  whiteSpace: "nowrap",
} as const;

const toneVariants = {
  subtle: {
    bg: "colorPalette.subtle",
    color: "colorPalette.fg",
    shadow: "none",
  },
  surface: {
    bg: "colorPalette.subtle",
    color: "colorPalette.fg",
    shadow: "inset 0 0 0 1px var(--color-border-2)",
  },
  outline: {
    bg: "transparent",
    color: "colorPalette.fg",
    shadow: "inset 0 0 0 1px var(--color-border)",
  },
  solid: {
    bg: "colorPalette.solid",
    color: "colorPalette.contrast",
    shadow: "none",
  },
  plain: {
    bg: "transparent",
    color: "colorPalette.fg",
    shadow: "none",
  },
} as const;

export const badgeRecipe = defineRecipe({
  base: pill,
  variants: {
    variant: toneVariants,
    size: {
      xs: {
        fontSize: "var(--text-11)",
        textStyle: "none",
        minH: "var(--space-4)",
        px: "var(--space-1)",
      },
      sm: {
        fontSize: "var(--text-12)",
        textStyle: "none",
        minH: "var(--space-5)",
        px: "var(--space-2)",
      },
      md: {
        fontSize: "var(--text-12)",
        textStyle: "none",
        minH: "var(--space-5)",
        px: "var(--space-2)",
      },
      lg: {
        fontSize: "var(--text-13)",
        textStyle: "none",
        minH: "var(--control-sm)",
        px: "var(--space-2)",
      },
    },
  },
});

const chipSize = ({
  height,
  fontSize,
}: {
  height: string;
  fontSize: string;
}) => ({
  root: {
    minH: height,
    px: "var(--space-2)",
    gap: "var(--space-1)",
    "--tag-element-size": "var(--icon-sm)",
    "--tag-avatar-size": "var(--icon-sm)",
    "--tag-element-offset": "0px",
  },
  label: { fontSize, textStyle: "none" },
});

export const tagSlotRecipe = defineSlotRecipe({
  slots: ["root", "label", "closeTrigger", "startElement", "endElement"],
  base: {
    root: {
      ...pill,
      focusVisibleRing: "none",
      _focusVisible: focusRing,
      // Selected chips sit on the accent tint, whatever their tone.
      "&[data-selected], &[aria-pressed=true]": {
        bg: "var(--color-accent-soft)",
        color: "var(--color-accent-soft-fg)",
      },
      // A disabled chip is not a control, so its words must stay readable:
      // fg-3 on surface-2 (4.5:1 and up), with a dashed outline to set it apart.
      _disabled: {
        color: "var(--color-fg-3)",
        bg: "var(--color-surface-2)",
        outline: "1px dashed var(--color-border)",
        outlineOffset: "-1px",
        cursor: "not-allowed",
      },
    },
    label: {
      lineClamp: "1",
      lineHeight: "var(--leading-tight)",
    },
    closeTrigger: {
      position: "relative",
      borderRadius: "var(--radius-pill)",
      color: "currentColor",
      focusVisibleRing: "none",
      _focusVisible: focusRing,
      _hover: { bg: "var(--color-surface-3)" },
      // The remove button takes presses across a 32px square, drawn by an
      // invisible centred box, so a small × is still easy to hit.
      _after: {
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        width: "max(100%, var(--hit-min))",
        height: "max(100%, var(--hit-min))",
        transform: "translate(-50%, -50%)",
      },
    },
  },
  variants: {
    variant: {
      subtle: { root: toneVariants.subtle },
      surface: { root: toneVariants.surface },
      outline: { root: toneVariants.outline },
      solid: { root: toneVariants.solid },
    },
    size: {
      sm: chipSize({ height: "var(--space-4)", fontSize: "var(--text-12)" }),
      md: chipSize({ height: "var(--space-5)", fontSize: "var(--text-12)" }),
      lg: chipSize({
        height: "var(--control-sm)",
        fontSize: "var(--text-13)",
      }),
      xl: chipSize({
        height: "var(--control-md)",
        fontSize: "var(--text-13)",
      }),
    },
  },
});
